import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    phoneNumber: '',
    birthMonth: '',
    birthDay: '',
    birthYear: '',
    password: '',
    confirmPassword: '',
    accountType: 'individual'
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const accountTypes = [
    { value: 'individual', label: 'Individual Donor' },
    { value: 'corporation', label: 'Corporation' },
    { value: 'nonprofit', label: 'Nonprofit Organization' }
  ];

  const months = [
    { value: '', label: 'Month' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const days = [{ value: '', label: 'Day' }, ...Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1)?.padStart(2, '0'),
    label: String(i + 1)
  }))];

  const currentYear = new Date()?.getFullYear();
  const years = [{ value: '', label: 'Year' }, ...Array.from({ length: 100 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i)
  }))];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // New handler specifically for Select components
  const handleSelectChange = (name) => (value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user makes a selection
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear birthday error when any birth field changes
    if (['birthMonth', 'birthDay', 'birthYear']?.includes(name) && errors?.birthday) {
      setErrors(prev => ({
        ...prev,
        birthday: ''
      }));
    }
  };

  const calculateAge = (month, day, year) => {
    if (!month || !day || !year) return null;
    
    const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    
    return age;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData?.displayName?.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]+$/?.test(formData?.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData?.birthMonth || !formData?.birthDay || !formData?.birthYear) {
      newErrors.birthday = 'Complete birthday is required';
    } else {
      let age = calculateAge(formData?.birthMonth, formData?.birthDay, formData?.birthYear);
      if (age === null) {
        newErrors.birthday = 'Invalid date of birth';
      } else if (age < 13) {
        newErrors.birthday = 'You must be at least 13 years old to create an account';
      }
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const { error } = await signUp(formData?.email, formData?.password, {
        data: {
          first_name: formData?.firstName,
          last_name: formData?.lastName,
          display_name: formData?.displayName,
          phone_number: formData?.phoneNumber,
          birth_date: `${formData?.birthYear}-${formData?.birthMonth}-${formData?.birthDay}`,
          account_type: formData?.accountType
        }
      });
      
      if (error) {
        setErrors({
          general: error?.message || 'Registration failed. Please try again.'
        });
      } else {
        // Navigate to confirmation screen with email
        navigate('/registration-confirmation', { 
          state: { email: formData?.email } 
        });
      }
    } catch (error) {
      setErrors({
        general: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="UserPlus" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-bold text-card-foreground mb-2">Create Account</h1>
          <p className="text-muted-foreground">Join Share Good and start making impact</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errors?.general && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{errors?.general}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="First Name"
              type="text"
              name="firstName"
              placeholder="Enter your first name"
              value={formData?.firstName}
              onChange={handleInputChange}
              error={errors?.firstName}
              required
              disabled={isLoading}
            />

            <Input
              label="Last Name"
              type="text"
              name="lastName"
              placeholder="Enter your last name"
              value={formData?.lastName}
              onChange={handleInputChange}
              error={errors?.lastName}
              required
              disabled={isLoading}
            />
          </div>

          <Input
            label="Display Name (Public Profile Name)"
            type="text"
            name="displayName"
            placeholder="Choose a display name for your profile"
            value={formData?.displayName}
            onChange={handleInputChange}
            error={errors?.displayName}
            required
            disabled={isLoading}
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="Enter your email"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
            disabled={isLoading}
          />

          <Input
            label="Phone Number"
            type="tel"
            name="phoneNumber"
            placeholder="Enter your phone number"
            value={formData?.phoneNumber}
            onChange={handleInputChange}
            error={errors?.phoneNumber}
            required
            disabled={isLoading}
          />

          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Date of Birth (Must be 13 or older)
            </label>
            <div className="grid grid-cols-3 gap-3">
              <Select
                name="birthMonth"
                value={formData?.birthMonth}
                onChange={handleSelectChange('birthMonth')}
                options={months}
                disabled={isLoading}
                placeholder="Month"
              />
              <Select
                name="birthDay"
                value={formData?.birthDay}
                onChange={handleSelectChange('birthDay')}
                options={days}
                disabled={isLoading}
                placeholder="Day"
              />
              <Select
                name="birthYear"
                value={formData?.birthYear}
                onChange={handleSelectChange('birthYear')}
                options={years}
                disabled={isLoading}
                placeholder="Year"
              />
            </div>
            {errors?.birthday && (
              <p className="mt-2 text-sm text-error flex items-center space-x-1">
                <Icon name="AlertCircle" size={16} />
                <span>{errors?.birthday}</span>
              </p>
            )}
          </div>

          <Select
            label="Account Type"
            name="accountType"
            value={formData?.accountType}
            onChange={handleSelectChange('accountType')}
            options={accountTypes}
            disabled={isLoading}
          />

          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Create a password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro"
              disabled={isLoading}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>

          <div className="relative">
            <Input
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData?.confirmPassword}
              onChange={handleInputChange}
              error={errors?.confirmPassword}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro"
              disabled={isLoading}
            >
              <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>

          <Button
            type="submit"
            fullWidth
            loading={isLoading}
            iconName="UserPlus"
            iconPosition="left"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-primary hover:text-primary/80 transition-micro font-medium"
                disabled={isLoading}
              >
                Sign in here
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;